import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Shield, Plus, Edit2, Trash2, X, Check, ChevronDown, ChevronRight,
  Users, Key, AlertCircle, Loader2
} from 'lucide-react';
import AdminSidebar from '../components/layout/AdminSidebar';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// Role color options
const ROLE_COLORS = [
  { value: 'slate', label: 'Gray', bg: 'bg-slate-100', text: 'text-slate-800' },
  { value: 'red', label: 'Red', bg: 'bg-red-100', text: 'text-red-800' },
  { value: 'orange', label: 'Orange', bg: 'bg-orange-100', text: 'text-orange-800' },
  { value: 'amber', label: 'Amber', bg: 'bg-amber-100', text: 'text-amber-800' },
  { value: 'green', label: 'Green', bg: 'bg-green-100', text: 'text-green-800' },
  { value: 'cyan', label: 'Cyan', bg: 'bg-cyan-100', text: 'text-cyan-800' },
  { value: 'blue', label: 'Blue', bg: 'bg-blue-100', text: 'text-blue-800' },
  { value: 'purple', label: 'Purple', bg: 'bg-purple-100', text: 'text-purple-800' },
  { value: 'pink', label: 'Pink', bg: 'bg-pink-100', text: 'text-pink-800' },
];

const getRoleColorClasses = (color) => {
  const colorMap = {
    slate: { bg: 'bg-slate-100', text: 'text-slate-800' },
    red: { bg: 'bg-red-100', text: 'text-red-800' },
    orange: { bg: 'bg-orange-100', text: 'text-orange-800' },
    amber: { bg: 'bg-amber-100', text: 'text-amber-800' },
    green: { bg: 'bg-green-100', text: 'text-green-800' },
    cyan: { bg: 'bg-cyan-100', text: 'text-cyan-800' },
    blue: { bg: 'bg-blue-100', text: 'text-blue-800' },
    purple: { bg: 'bg-purple-100', text: 'text-purple-800' },
    pink: { bg: 'bg-pink-100', text: 'text-pink-800' },
  };
  return colorMap[color] || colorMap.slate;
};

const AdminRolesPage = () => {
  const navigate = useNavigate();
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState({});
  const [groupedPermissions, setGroupedPermissions] = useState({});
  const [loading, setLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState(null);
  
  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('create'); // 'create' or 'edit'
  const [editingRole, setEditingRole] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    permissions: [],
    color: 'slate'
  });
  const [formError, setFormError] = useState('');
  const [formLoading, setFormLoading] = useState(false);
  
  // Expanded permission categories
  const [expandedCategories, setExpandedCategories] = useState([]);

  useEffect(() => {
    const isAdmin = localStorage.getItem('adminLoggedIn');
    if (!isAdmin) {
      navigate('/admin/login');
      return;
    }

    const userStr = localStorage.getItem('adminUser');
    if (userStr) {
      setCurrentUser(JSON.parse(userStr));
    }

    fetchData();
  }, [navigate]);

  const fetchData = async () => {
    try {
      const [rolesRes, permissionsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/roles`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/roles/permissions`, { withCredentials: true })
      ]);
      setRoles(rolesRes.data);
      setPermissions(permissionsRes.data.permissions);
      setGroupedPermissions(permissionsRes.data.grouped);
      // Expand all categories by default
      setExpandedCategories(Object.keys(permissionsRes.data.grouped));
    } catch (error) {
      console.error('Error fetching data:', error);
      if (error.response?.status === 401) {
        localStorage.removeItem('adminLoggedIn');
        navigate('/admin/login');
      }
    } finally {
      setLoading(false);
    }
  };

  const toggleCategory = (category) => {
    setExpandedCategories(prev =>
      prev.includes(category)
        ? prev.filter(c => c !== category)
        : [...prev, category]
    );
  };

  const openCreateModal = () => {
    setModalMode('create');
    setEditingRole(null);
    setFormData({
      name: '',
      description: '',
      permissions: [],
      color: 'slate'
    });
    setFormError('');
    setShowModal(true);
  };

  const openEditModal = (role) => {
    setModalMode('edit');
    setEditingRole(role);
    setFormData({
      name: role.name,
      description: role.description,
      permissions: [...role.permissions],
      color: role.color || 'slate'
    });
    setFormError('');
    setShowModal(true);
  };

  const handlePermissionToggle = (permissionId) => {
    setFormData(prev => ({
      ...prev,
      permissions: prev.permissions.includes(permissionId)
        ? prev.permissions.filter(p => p !== permissionId)
        : [...prev.permissions, permissionId]
    }));
  };

  const handleCategoryToggle = (category) => {
    const categoryPerms = groupedPermissions[category]?.map(p => p.id) || [];
    const allSelected = categoryPerms.every(p => formData.permissions.includes(p));
    
    setFormData(prev => ({
      ...prev,
      permissions: allSelected
        ? prev.permissions.filter(p => !categoryPerms.includes(p))
        : [...new Set([...prev.permissions, ...categoryPerms])]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');
    setFormLoading(true);

    try {
      if (modalMode === 'create') {
        await axios.post(`${BACKEND_URL}/api/roles`, formData, { withCredentials: true });
      } else {
        await axios.put(`${BACKEND_URL}/api/roles/${editingRole.id}`, formData, { withCredentials: true });
      }
      setShowModal(false);
      fetchData();
    } catch (error) {
      setFormError(error.response?.data?.detail || 'Failed to save role');
    } finally {
      setFormLoading(false);
    }
  };

  const handleDelete = async (role) => {
    if (role.is_system) {
      alert('System roles cannot be deleted.');
      return;
    }

    if (!window.confirm(`Are you sure you want to delete the "${role.name}" role? This action cannot be undone.`)) {
      return;
    }

    try {
      await axios.delete(`${BACKEND_URL}/api/roles/${role.id}`, { withCredentials: true });
      fetchData();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to delete role');
    }
  };

  // Check if current user has permission to manage roles
  const canManageRoles = currentUser?.role === 'super_admin' || 
    currentUser?.permissions?.includes('manage_roles');

  return (
    <AdminSidebar pageTitle="Roles & Permissions" pageIcon={Shield}>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <p className="text-slate-600">
            Manage user roles and their permissions across the system.
          </p>
        </div>
        {canManageRoles && (
          <Button onClick={openCreateModal} className="bg-cyan-600 hover:bg-cyan-700">
            <Plus className="w-4 h-4 mr-2" /> Create Role
          </Button>
        )}
      </div>

      {/* Info Banner */}
      <div className="bg-cyan-50 border border-cyan-200 rounded-lg p-4 mb-6">
        <div className="flex items-start gap-3">
          <Key className="w-5 h-5 text-cyan-600 mt-0.5" />
          <div>
            <h4 className="font-medium text-cyan-900">About Roles & Permissions</h4>
            <p className="text-sm text-cyan-700 mt-1">
              Roles define what users can access and do in the system. Assign roles when inviting new users, 
              and users will inherit all permissions from their assigned role.
            </p>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-cyan-600" />
        </div>
      ) : (
        <div className="grid gap-4">
          {roles.map((role) => {
            const colorClasses = getRoleColorClasses(role.color);
            return (
              <div
                key={role.id}
                className="bg-white rounded-xl shadow-sm border border-slate-200 p-6"
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-4">
                    <div className={`w-12 h-12 rounded-lg ${colorClasses.bg} flex items-center justify-center`}>
                      <Shield className={`w-6 h-6 ${colorClasses.text}`} />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="text-lg font-semibold text-slate-900">{role.name}</h3>
                        {role.is_system && (
                          <span className="px-2 py-0.5 bg-slate-100 text-slate-600 text-xs rounded-full font-medium">
                            System
                          </span>
                        )}
                        <span className={`px-2 py-0.5 ${colorClasses.bg} ${colorClasses.text} text-xs rounded-full font-medium`}>
                          {role.permissions?.length || 0} permissions
                        </span>
                      </div>
                      <p className="text-slate-600 text-sm mt-1">{role.description}</p>
                      
                      {/* Permission tags */}
                      <div className="flex flex-wrap gap-1.5 mt-3">
                        {role.permissions?.slice(0, 5).map((perm) => (
                          <span
                            key={perm}
                            className="px-2 py-0.5 bg-slate-100 text-slate-600 text-xs rounded"
                          >
                            {permissions[perm]?.name || perm}
                          </span>
                        ))}
                        {role.permissions?.length > 5 && (
                          <span className="px-2 py-0.5 bg-slate-100 text-slate-500 text-xs rounded">
                            +{role.permissions.length - 5} more
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  {canManageRoles && (
                    <div className="flex items-center gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openEditModal(role)}
                        className="text-slate-600 hover:text-slate-900"
                      >
                        <Edit2 className="w-4 h-4" />
                      </Button>
                      {!role.is_system && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(role)}
                          className="text-red-600 hover:text-red-700"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })}

          {roles.length === 0 && (
            <div className="bg-white rounded-xl p-12 text-center">
              <Shield className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-slate-900 mb-2">No Roles Found</h3>
              <p className="text-slate-600">Create your first role to get started.</p>
            </div>
          )}
        </div>
      )}

      {!canManageRoles && (
        <div className="mt-6 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-yellow-600 mt-0.5" />
            <p className="text-yellow-800 text-sm">
              <strong>Note:</strong> You don't have permission to manage roles. 
              Contact a super admin if you need to make changes.
            </p>
          </div>
        </div>
      )}

      {/* Create/Edit Role Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            {/* Modal Header */}
            <div className="p-6 border-b flex-shrink-0">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold text-slate-900">
                  {modalMode === 'create' ? 'Create New Role' : 'Edit Role'}
                </h2>
                <button
                  onClick={() => setShowModal(false)}
                  className="text-slate-400 hover:text-slate-600"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Modal Body */}
            <form onSubmit={handleSubmit} className="flex flex-col flex-1 overflow-hidden">
              <div className="p-6 space-y-6 overflow-y-auto flex-1">
                {/* Role Name */}
                <div>
                  <Label htmlFor="name">Role Name *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    className="mt-1"
                    placeholder="e.g., Marketing Manager"
                    disabled={editingRole?.is_system}
                  />
                  {editingRole?.is_system && (
                    <p className="text-sm text-amber-600 mt-1">System role names cannot be changed.</p>
                  )}
                </div>

                {/* Description */}
                <div>
                  <Label htmlFor="description">Description</Label>
                  <Input
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="mt-1"
                    placeholder="Describe what this role is for..."
                  />
                </div>

                {/* Color Selection */}
                <div>
                  <Label>Role Color</Label>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {ROLE_COLORS.map((color) => (
                      <button
                        key={color.value}
                        type="button"
                        onClick={() => setFormData({ ...formData, color: color.value })}
                        className={`w-8 h-8 rounded-full ${color.bg} border-2 transition-all ${
                          formData.color === color.value
                            ? 'border-slate-900 scale-110'
                            : 'border-transparent hover:scale-105'
                        }`}
                        title={color.label}
                      />
                    ))}
                  </div>
                </div>

                {/* Permissions */}
                <div>
                  <Label className="mb-3 block">Permissions</Label>
                  <div className="border rounded-lg divide-y">
                    {Object.entries(groupedPermissions).map(([category, perms]) => {
                      const isExpanded = expandedCategories.includes(category);
                      const selectedCount = perms.filter(p => formData.permissions.includes(p.id)).length;
                      const allSelected = selectedCount === perms.length;
                      const someSelected = selectedCount > 0 && !allSelected;

                      return (
                        <div key={category}>
                          <button
                            type="button"
                            onClick={() => toggleCategory(category)}
                            className="w-full flex items-center justify-between p-4 hover:bg-slate-50 transition-colors"
                          >
                            <div className="flex items-center gap-3">
                              {isExpanded ? (
                                <ChevronDown className="w-4 h-4 text-slate-400" />
                              ) : (
                                <ChevronRight className="w-4 h-4 text-slate-400" />
                              )}
                              <span className="font-medium text-slate-900">{category}</span>
                              <span className="text-sm text-slate-500">
                                ({selectedCount}/{perms.length})
                              </span>
                            </div>
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleCategoryToggle(category);
                              }}
                              className={`px-3 py-1 text-xs rounded-full font-medium transition-colors ${
                                allSelected
                                  ? 'bg-cyan-100 text-cyan-700'
                                  : someSelected
                                  ? 'bg-amber-100 text-amber-700'
                                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                              }`}
                            >
                              {allSelected ? 'Deselect All' : 'Select All'}
                            </button>
                          </button>

                          {isExpanded && (
                            <div className="px-4 pb-4 space-y-2">
                              {perms.map((perm) => (
                                <label
                                  key={perm.id}
                                  className="flex items-start gap-3 p-3 rounded-lg hover:bg-slate-50 cursor-pointer transition-colors"
                                >
                                  <Checkbox
                                    checked={formData.permissions.includes(perm.id)}
                                    onCheckedChange={() => handlePermissionToggle(perm.id)}
                                    className="mt-0.5"
                                  />
                                  <div>
                                    <div className="font-medium text-slate-900 text-sm">{perm.name}</div>
                                    <div className="text-slate-500 text-xs">{perm.description}</div>
                                  </div>
                                </label>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>

                {formError && (
                  <div className="text-red-600 text-sm bg-red-50 p-3 rounded-lg flex items-center gap-2">
                    <AlertCircle className="w-4 h-4" />
                    {formError}
                  </div>
                )}
              </div>

              {/* Modal Footer */}
              <div className="p-6 border-t bg-slate-50 flex-shrink-0">
                <div className="flex gap-3">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setShowModal(false)}
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    className="flex-1 bg-cyan-600 hover:bg-cyan-700"
                    disabled={formLoading}
                  >
                    {formLoading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <Check className="w-4 h-4 mr-2" />
                        {modalMode === 'create' ? 'Create Role' : 'Save Changes'}
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}
    </AdminSidebar>
  );
};

export default AdminRolesPage;
